import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type RefObject,
} from "react";
import {
  defaultMediaPreferences,
  readMediaPreferences,
  writeMediaPreferences,
} from "../lib/mediaPreferences";

export type BackgroundMediaState = "loading" | "ready" | "unavailable";

export type BackgroundMediaController = {
  readonly enabled: boolean;
  readonly videoRef: RefObject<HTMLVideoElement | null>;
  readonly state: BackgroundMediaState;
  readonly isMuted: boolean;
  readonly volume: number;
  readonly markReady: () => void;
  readonly markUnavailable: () => void;
  readonly toggleSound: () => Promise<void>;
  readonly setVolume: (value: number) => Promise<void>;
};

function subscribeToMotion(onChange: () => void) {
  const preference = window.matchMedia?.("(prefers-reduced-motion: reduce)");
  preference?.addEventListener("change", onChange);
  return () => preference?.removeEventListener("change", onChange);
}

function allowBackgroundMedia() {
  const connection = (
    navigator as Navigator & { connection?: { saveData?: boolean } }
  ).connection;
  return (
    !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches &&
    !connection?.saveData
  );
}

const serverMediaEnabled = () => false;

export function useBackgroundMedia(): BackgroundMediaController {
  const enabled = useSyncExternalStore(
    subscribeToMotion,
    allowBackgroundMedia,
    serverMediaEnabled,
  );
  const videoRef = useRef<HTMLVideoElement>(null);
  const [initialPreferences] = useState(readMediaPreferences);
  const [state, setState] = useState<BackgroundMediaState>("loading");
  const [isMuted, setIsMuted] = useState(initialPreferences.muted);
  const [volume, setVolumeValue] = useState(initialPreferences.volume);

  const markReady = useCallback(() => setState("ready"), []);
  const markUnavailable = useCallback(() => setState("unavailable"), []);

  useEffect(() => {
    if (videoRef.current) videoRef.current.volume = volume;
  }, [enabled, volume]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const startPlayback = async () => {
      try {
        await video.play();
      } catch {
        if (video.error) {
          markUnavailable();
          return;
        }

        // Browsers may reject restored, unmuted autoplay. Keep the saved
        // preference, but fall back to muted playback for this page load.
        video.muted = true;
        setIsMuted(true);

        try {
          await video.play();
        } catch {
          if (video.error) markUnavailable();
        }
      }
    };

    void startPlayback();
  }, [enabled, markUnavailable]);

  const toggleSound = useCallback(async () => {
    const video = videoRef.current;
    if (!video || state === "unavailable") return;

    const nextMuted = !isMuted;
    const nextVolume =
      !nextMuted && volume === 0 ? defaultMediaPreferences.volume : volume;

    video.volume = nextVolume;
    video.muted = nextMuted;
    setVolumeValue(nextVolume);
    setIsMuted(nextMuted);
    writeMediaPreferences({ muted: nextMuted, volume: nextVolume });

    if (!video.paused) return;

    try {
      await video.play();
      markReady();
    } catch {
      if (video.error) markUnavailable();
      video.muted = true;
      setIsMuted(true);
    }
  }, [isMuted, markReady, markUnavailable, state, volume]);

  const setVolume = useCallback(
    async (value: number) => {
      const video = videoRef.current;
      if (!video || state === "unavailable") return;

      const nextVolume = Math.min(1, Math.max(0, value));
      const nextMuted = nextVolume === 0;

      video.volume = nextVolume;
      video.muted = nextMuted;
      setVolumeValue(nextVolume);
      setIsMuted(nextMuted);
      writeMediaPreferences({ muted: nextMuted, volume: nextVolume });

      if (nextMuted || !video.paused) return;

      try {
        await video.play();
        markReady();
      } catch {
        if (video.error) markUnavailable();
        video.muted = true;
        setIsMuted(true);
      }
    },
    [markReady, markUnavailable, state],
  );

  return {
    enabled,
    videoRef,
    state,
    isMuted: enabled ? isMuted : defaultMediaPreferences.muted,
    volume: enabled ? volume : defaultMediaPreferences.volume,
    markReady,
    markUnavailable,
    toggleSound,
    setVolume,
  };
}
